import { useState, type ReactNode } from 'react'
import { Button, Card, LoadingBlock, track } from '@quoter/web-core'
import { useI18n } from '@/hooks/useI18n'
import { useJourney, useSaveJourney } from '@/hooks/useJourney'
import { legalUrls } from '@/lib/product'

/**
 * Stands in front of every screen that stores what the person eats or feels.
 * That is health data, and the law (GDPR Art. 9) wants explicit consent for
 * it — a deliberate tick, not a line in the small print — so nothing can be
 * logged until both boxes are ticked. The moment is recorded on the journey
 * row; deleting one's data in Settings removes it and this returns.
 *
 * The food table and Settings sit outside the gate: they store nothing.
 */
export function ConsentGate({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n()
  const { journey, loading } = useJourney()
  const save = useSaveJourney()
  const [data, setData] = useState(false)
  const [notMedical, setNotMedical] = useState(false)

  if (loading) return <LoadingBlock />
  if (journey.consentAt) return <>{children}</>

  const urls = legalUrls(locale)
  const link = 'font-semibold text-brand-700 underline underline-offset-2'

  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <h1 className="font-display text-[26px] font-normal tracking-tightish text-gray-900">{t.consent.title}</h1>
        <p className="mt-3 text-[14px] leading-relaxed text-gray-700">{t.consent.intro}</p>

        <div className="mt-5 space-y-3">
          <ConsentBox checked={data} onChange={setData}>
            {t.consent.data}
          </ConsentBox>
          <ConsentBox checked={notMedical} onChange={setNotMedical}>
            {t.consent.notMedical}
          </ConsentBox>
        </div>

        <p className="mt-5 text-[12.5px] leading-relaxed text-gray-500">
          {t.consent.readMore}{' '}
          <a href={urls.privacy} target="_blank" rel="noopener" className={link}>
            {t.legal.privacy}
          </a>{' '}
          ·{' '}
          <a href={urls.terms} target="_blank" rel="noopener" className={link}>
            {t.legal.terms}
          </a>
        </p>

        <div className="mt-6 flex items-center gap-3">
          <Button
            size="lg"
            disabled={!data || !notMedical || save.isPending}
            onClick={() => save.mutate({ consentAt: new Date().toISOString() }, { onSuccess: () => track('consent_given') })}
          >
            {save.isPending ? t.common.saving : t.consent.agree}
          </Button>
          {save.isError && (
            <span className="text-[12.5px] text-red-700" role="alert">
              {t.common.error}
            </span>
          )}
        </div>
        <p className="mt-4 text-[12.5px] text-gray-500">{t.consent.withdraw}</p>
      </Card>
    </div>
  )
}

function ConsentBox({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  children: ReactNode
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md bg-surface-muted px-4 py-3.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong accent-[rgb(var(--c-sage-700))]"
      />
      <span className="text-[13.5px] leading-relaxed text-gray-800">{children}</span>
    </label>
  )
}
